import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { Users as UsersIcon, Search, Plus, Pencil, Trash2, Shield, ShieldOff } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { clearCache, CACHE_KEYS } from '../../services/cacheService';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

interface AdminUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  department: string | null;
  position: string | null;
  city: string | null;
  mobile_number: string | null;
  is_admin: boolean;
  created_at: string;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  department: '',
  position: '',
  city: '',
  mobile_number: '',
  is_admin: false,
};

const AdminUsers: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page on search change
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      params.set('page', currentPage.toString());

      const res = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        throw new Error(`Error ${res.status}`);
      }
      const data = await res.json();
      setUsers(data.data || []);
      setPaginationMeta({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (err: any) {
      setError(err.message || t('common:errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Open create dialog
  const handleCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setFormErrors({});
    setFormOpen(true);
  };

  // Open edit dialog
  const handleEdit = (user: AdminUser) => {
    setEditingUser(user);
    setForm({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      password: '',
      department: user.department || '',
      position: user.position || '',
      city: user.city || '',
      mobile_number: user.mobile_number || '',
      is_admin: user.is_admin,
    });
    setFormErrors({});
    setFormOpen(true);
  };

  // Submit form
  const handleSubmit = async () => {
    setSaving(true);
    setFormErrors({});
    try {
      const isEdit = !!editingUser;
      const url = isEdit
        ? `${API_BASE_URL}/admin/users/${editingUser!.id}`
        : `${API_BASE_URL}/admin/users`;

      const body: Record<string, any> = { ...form };
      // Don't send empty password on edit
      if (isEdit && !body.password) delete body.password;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        const data = await res.json();
        if (data.errors) {
          const errs: Record<string, string> = {};
          Object.entries(data.errors).forEach(([k, v]) => {
            errs[k] = Array.isArray(v) ? v[0] : String(v);
          });
          setFormErrors(errs);
        } else {
          throw new Error(data.message || t('common:errors.generic'));
        }
        return;
      }

      setFormOpen(false);
      clearCache(CACHE_KEYS.USERS);
      fetchUsers();
    } catch (err: any) {
      setFormErrors({ _general: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDeleteClick = (user: AdminUser) => {
    setUserToDelete(user);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        const data = await res.json();
        throw new Error(data.message || t('common:errors.generic'));
      }
      setDeleteOpen(false);
      setUserToDelete(null);
      clearCache(CACHE_KEYS.USERS);
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('admin:users.page_title')}</h1>
          <p className="text-muted-foreground">{t('admin:users.page_subtitle')}</p>
        </div>
        <div className="flex flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('admin:users.search_placeholder')}
              className="pl-9 w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" /> {t('admin:users.new_user')}
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>{error}</p>
              <Button variant="outline" onClick={fetchUsers} className="mt-4">{t('common:actions.try_again')}</Button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('admin:users.no_users_found')}</h3>
              <p className="text-muted-foreground">{t('admin:users.no_users_hint')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin:users.table_name')}</TableHead>
                    <TableHead>{t('admin:users.table_email')}</TableHead>
                    <TableHead>{t('admin:users.table_department')}</TableHead>
                    <TableHead>{t('admin:users.table_position')}</TableHead>
                    <TableHead>{t('admin:users.table_role')}</TableHead>
                    <TableHead className="text-center">{t('admin:users.table_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.department || '—'}</TableCell>
                      <TableCell>{u.position || '—'}</TableCell>
                      <TableCell>
                        {u.is_admin ? (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                            <Shield className="h-3 w-3 mr-1" /> {t('admin:users.role_admin')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">{t('admin:users.role_user')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-blue-50" title={t('common:actions.edit')} onClick={() => handleEdit(u)}>
                            <Pencil className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-red-50" title={t('common:actions.delete')} onClick={() => handleDeleteClick(u)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {paginationMeta && paginationMeta.last_page > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t('admin:users.showing_users', {
                  start: ((paginationMeta.current_page - 1) * paginationMeta.per_page) + 1,
                  end: Math.min(paginationMeta.current_page * paginationMeta.per_page, paginationMeta.total),
                  total: paginationMeta.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-4 w-4" /> {t('common:pagination.previous')}
                </Button>
                <span className="text-sm font-medium">
                  {t('common:pagination.page_of', { current: paginationMeta.current_page, last: paginationMeta.last_page })}
                </span>
                <Button variant="outline" size="sm" disabled={currentPage >= paginationMeta.last_page} onClick={() => setCurrentPage((p) => Math.min(paginationMeta.last_page, p + 1))}>
                  {t('common:pagination.next')} <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? t('admin:users.edit_user') : t('admin:users.create_user')}</DialogTitle>
            <DialogDescription>
              {editingUser ? t('admin:users.edit_description') : t('admin:users.create_description')}
            </DialogDescription>
          </DialogHeader>

          {formErrors._general && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{formErrors._general}</div>
          )}

          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">{t('admin:users.form_first_name')} *</Label>
                <Input id="first_name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                {formErrors.first_name && <p className="text-xs text-red-500 mt-1">{formErrors.first_name}</p>}
              </div>
              <div>
                <Label htmlFor="last_name">{t('admin:users.form_last_name')} *</Label>
                <Input id="last_name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                {formErrors.last_name && <p className="text-xs text-red-500 mt-1">{formErrors.last_name}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="email">{t('admin:users.form_email')} *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
            </div>
            <div>
              <Label htmlFor="password">{editingUser ? t('admin:users.form_password_edit') : `${t('admin:users.form_password')} *`}</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              {formErrors.password && <p className="text-xs text-red-500 mt-1">{formErrors.password}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="department">{t('admin:users.form_department')}</Label>
                <Input id="department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="position">{t('admin:users.form_position')}</Label>
                <Input id="position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">{t('admin:users.form_city')}</Label>
                <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="mobile_number">{t('admin:users.form_mobile_number')}</Label>
                <Input id="mobile_number" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-2">
                {form.is_admin ? <Shield className="h-4 w-4 text-purple-600" /> : <ShieldOff className="h-4 w-4 text-slate-400" />}
                <div>
                  <Label htmlFor="is_admin" className="cursor-pointer">{t('admin:users.form_is_admin')}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin:users.form_is_admin_description')}</p>
                </div>
              </div>
              <Switch id="is_admin" checked={form.is_admin} onCheckedChange={(checked) => setForm({ ...form, is_admin: checked })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? t('common:actions.saving') : editingUser ? t('common:actions.update') : t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin:users.delete_user')}</DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="admin:users.delete_confirm"
                values={{ name: `${userToDelete?.first_name} ${userToDelete?.last_name}` }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>{t('common:actions.cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? t('common:actions.deleting') : t('common:actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
